// @flow
import { createRef, useState, useEffect } from "react";
import { TextInput, ADDRESS_NOT_FOUND_ID } from "@nested/brand";
import { Heading, QuestionWrapper } from "../components";
import { useDesktopFocus } from "../hooks/useDesktopFocus";
import { type QuestionProps } from "../components/MultiStageForm";
import { validatePresence } from "./validators";
import { usePrefilledAddress } from "../hooks/usePrefilledAddress";

const validate = async (value = {}) => {
  const lineOne = await validatePresence("lineOne")(value?.lineOne);
  const city = await validatePresence("city")(value?.city);
  const postcode = await validatePresence("postcode")(value?.postcode);

  return { ...lineOne, ...city, ...postcode };
};

export const ManualOnboardingAddress = ({
  ActionButtons,
  BackButton,
  initialValue,
  next,
  validationErrors,
}: QuestionProps<"manualAddress">) => {
  const [lineOne, setLineOne] = useState(initialValue?.lineOne || "");
  const [lineTwo, setLineTwo] = useState(initialValue?.lineTwo || "");
  const [city, setCity] = useState(initialValue?.city || "");
  const [postcode, setPostcode] = useState(initialValue?.postcode || "");

  const ref = createRef();
  useDesktopFocus(ref);

  const { address: prefilledAddress, loading } = usePrefilledAddress(true);

  useEffect(() => {
    if (!loading && prefilledAddress) {
      setPostcode(prefilledAddress.postcode || "");
    }
  }, [loading, prefilledAddress]);

  if (loading) return null;

  return (
    <QuestionWrapper
      next={() =>
        next(
          {
            id: ADDRESS_NOT_FOUND_ID,
            lineOne,
            lineTwo,
            city,
            postcode,
          },
          validate,
        )
      }
      dataTest="manual-address-entry"
    >
      <BackButton />
      <Heading>What's your full address?</Heading>
      <TextInput
        className="fs-exclude"
        errorMessage={validationErrors.lineOne}
        hasErrors={Boolean(validationErrors.lineOne)}
        onChange={(e) => setLineOne(e.target.value)}
        placeholder="Address line one"
        ref={ref}
        type="text"
        value={lineOne}
      />
      <TextInput
        css="margin-top: 30px;"
        onChange={(e) => setLineTwo(e.target.value)}
        errorMessage={validationErrors.lineTwo}
        hasErrors={Boolean(validationErrors.lineTwo)}
        placeholder="Address line two (optional)"
        type="text"
        value={lineTwo}
      />
      <TextInput
        css="margin-top: 30px;"
        errorMessage={validationErrors.city}
        hasErrors={Boolean(validationErrors.city)}
        onChange={(e) => setCity(e.target.value)}
        placeholder="Town or city"
        type="text"
        value={city}
      />
      <TextInput
        css="margin-top: 30px;"
        onChange={(e) => setPostcode(e.target.value.toUpperCase())}
        errorMessage={validationErrors.postcode}
        hasErrors={Boolean(validationErrors.postcode)}
        placeholder="Postcode"
        type="text"
        value={postcode}
      />
      <ActionButtons />
    </QuestionWrapper>
  );
};
